import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { fetchElevationGrid } from '../utils/gugikNmt';
import { saveTile } from '../firebase/nmtTiles';

const NmtDownloadContext = createContext(null);

export function NmtDownloadProvider({ children }) {
  // Aktualnie aktywne pobranie (max 1 naraz). Trzymane w stanie żeby UI banneru reagował.
  // { fileName, done, total, projectId, bbox, step, status: 'fetching'|'saving'|'done'|'error', error?, startedAt }
  const [download, setDownload] = useState(null);
  const cancelRef = useRef(false);

  const startTileDownload = useCallback(async ({ projectId, bbox, step, xCoords, yCoords, onSuccess, onError }) => {
    if (download && download.status !== 'done' && download.status !== 'error') {
      throw new Error('Inne pobranie NMT już trwa — poczekaj aż się zakończy');
    }
    cancelRef.current = false;
    const total = xCoords.length * yCoords.length;
    const startedAt = Date.now();
    setDownload({
      fileName: `tile_${Math.round(bbox.minX)}_${Math.round(bbox.minY)}_${Math.round(bbox.maxX)}_${Math.round(bbox.maxY)}_${step}.bin`,
      done: 0,
      total,
      projectId,
      bbox,
      step,
      status: 'fetching',
      startedAt,
    });

    try {
      const { grid } = await fetchElevationGrid(xCoords, yCoords, {
        onProgress: (done, t) => {
          if (cancelRef.current) return;
          setDownload(d => d ? { ...d, done, total: t } : null);
        },
      });
      if (cancelRef.current) {
        setDownload(null);
        return;
      }
      setDownload(d => d ? { ...d, status: 'saving' } : null);
      const fileName = await saveTile(projectId, bbox, step, grid);
      setDownload(d => d ? { ...d, status: 'done', fileName } : null);
      // Auto-clear po 3 sekundach
      setTimeout(() => setDownload(d => (d && d.status === 'done' ? null : d)), 3000);
      if (onSuccess) onSuccess({ fileName, grid, projectId, bbox, step });
    } catch (err) {
      console.error('NmtDownloadContext: fetch error', err);
      setDownload(d => d ? { ...d, status: 'error', error: err.message || 'Nie udało się pobrać NMT' } : null);
      if (onError) onError(err);
    }
  }, [download]);

  const cancelDownload = useCallback(() => {
    cancelRef.current = true;
    setDownload(null);
  }, []);

  const dismissError = useCallback(() => {
    setDownload(d => (d && d.status === 'error' ? null : d));
  }, []);

  return (
    <NmtDownloadContext.Provider value={{ download, startTileDownload, cancelDownload, dismissError }}>
      {children}
    </NmtDownloadContext.Provider>
  );
}

export function useNmtDownload() {
  const ctx = useContext(NmtDownloadContext);
  if (!ctx) throw new Error('useNmtDownload musi być użyte wewnątrz <NmtDownloadProvider>');
  return ctx;
}

/**
 * Banner pokazywany na górze ekranu gdy trwa pobranie NMT (lub jest błąd).
 * Renderowany w App.js — persystuje przez nawigację.
 */
export function NmtDownloadBanner() {
  const { download, dismissError } = useNmtDownload();
  if (!download) return null;

  const pct = download.total > 0 ? Math.round((download.done / download.total) * 100) : 0;
  const elapsedSec = Math.floor((Date.now() - download.startedAt) / 1000);
  const eta = download.done > 0 && download.status === 'fetching'
    ? Math.max(0, Math.ceil((elapsedSec / download.done) * (download.total - download.done)))
    : null;

  let bg, text, label;
  if (download.status === 'error') {
    bg = 'bg-red-600';
    text = 'text-white';
    label = `❌ Błąd pobierania NMT: ${download.error}`;
  } else if (download.status === 'done') {
    bg = 'bg-green-600';
    text = 'text-white';
    label = `✓ Pobrano i zapisano tile NMT (${download.total.toLocaleString()} pkt)`;
  } else if (download.status === 'saving') {
    bg = 'bg-blue-600';
    text = 'text-white';
    label = `💾 Zapisywanie tile NMT do Firebase Storage...`;
  } else {
    bg = 'bg-purple-600';
    text = 'text-white';
    label = `⛰️ Pobieranie NMT z GUGiK: ${download.done.toLocaleString()}/${download.total.toLocaleString()} (${pct}%)${eta !== null ? `, ETA ~${eta >= 60 ? Math.ceil(eta / 60) + ' min' : eta + ' s'}` : ''}`;
  }

  return (
    <div className={`fixed top-0 left-0 right-0 z-50 ${bg} ${text} shadow-lg`}>
      <div className="max-w-7xl mx-auto px-4 py-2 flex items-center gap-3">
        <span className="text-sm font-medium flex-1">{label}</span>
        {download.status === 'fetching' && (
          <div className="hidden sm:block w-48 h-2 bg-white/30 rounded overflow-hidden">
            <div className="h-full bg-white transition-all" style={{ width: `${pct}%` }} />
          </div>
        )}
        {(download.status === 'error' || download.status === 'done') && (
          <button
            onClick={dismissError}
            className="px-2 py-0.5 text-xs rounded bg-white/20 hover:bg-white/30"
          >
            ✕
          </button>
        )}
      </div>
    </div>
  );
}
